import { memo, useCallback, useMemo, useRef, useState } from "react";

import { getKpdsAPI } from "services/api/finance/billing/api";

import { DropdownAutocomplete } from "ui/components/Dropdown/DropdownAutocomplete";
import { DropdownOption } from "ui/components/Dropdown/DropdownAutocomplete/types";

import { KpdDropdownProps } from "./types";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

/**
 * KPD oznaka stavke — obavezna na eRačunu (HR-BR-25). Šifarnik ima stotine redaka, pa se
 * pretražuje na poslužitelju. Ako tražena djelatnost još nije u šifarniku, upisana šifra
 * se svejedno zadržava.
 */
const KpdDropdown: React.FC<KpdDropdownProps> = (props) => {
  const { name, value, onChange, label, placeholder = "Pretraži KPD šifru ili naziv", error, disabled, required } = props;

  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchKpds = useCallback(async (search: string) => {
    setIsLoading(true);

    try {
      const results = await getKpdsAPI(search);
      setOptions(results.map((kpd) => ({ value: kpd.code, text: `${kpd.code} — ${kpd.name}` })));
    } catch {
      setOptions([]);
    }

    setIsLoading(false);
  }, []);

  const handleSearchChange = useCallback((searchValue: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = searchValue.trim();

    if (term.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(() => fetchKpds(term), SEARCH_DEBOUNCE_MS);
  }, [fetchKpds]);

  const handleSearchFocus = useCallback(() => {}, []);

  // Već odabrana šifra mora ostati vidljiva i kad lista rezultata nije napunjena.
  const displayOptions = useMemo(() => {
    if (value && !options.some((option) => option.value === value)) {
      return [{ value, text: value }, ...options];
    }

    return options;
  }, [value, options]);

  return (
    <DropdownAutocomplete
      name={name}
      value={value}
      options={displayOptions}
      onChange={onChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      label={label}
      placeholder={placeholder}
      error={error}
      disabled={disabled}
      required={required}
      isLoading={isLoading}
    />
  );
};

export default memo(KpdDropdown);
